import React, { useState, useEffect, useContext, useMemo } from 'react';
import { Route, Routes, useLocation } from 'react-router-dom';
import PropTypes from 'prop-types';
import { getCookie, setCookie } from 'typescript-cookie';

import Dialog from '@/components/ui-bridge/core/Dialog';
import AcceptedTermsModal from './components/AcceptedTermsModal';
import ClientMenu from './components/ClientMenu/ClientMenu';
import FeatureModal from './components/FeatureModal';
import MarketingModal from './components/MarketingModal';
import Grid from '@/components/ui-bridge/core/Grid';
import NewRvPanelFeatureBar from './components/NewRvPanelFeatureBar';
import UserDashboardsWrapper from './components/UserDashboardsWrapper';
import { CLIENT_FORM_URL, CLIENT_FORM_TITLE, RV_GROUP_CLIENT_PANEL_BASE_URL } from './config';
import ClientFinancialSummary from './containers/ClientFinancialSummary/ClientFinancialSummary';
import ClientNewOrder from './containers/ClientNewOrder/ClientNewOrder';
import ClientOrders from './containers/ClientOrders/ClientOrders';
import ApiContext from './contexts/ApiContext';
import UserContext from './contexts/UserContext';
import Header from './Header';
import ClientFormSuccess from './orderForm/ClientOrderForm/ClientFormSuccess';

const ClientApp = ({ balance, contracts, features, userName }) => {
  const me = useContext(UserContext);
  const location = useLocation();

  const [apiStatus] = useState('IDLE');
  const [showModal, setShowModal] = useState(false);
  const [showMarketingModal, setShowMarketingModal] = useState(false);

  useEffect(() => {
    // NPS modal logic
    const campaignEnd = new Date(2026, 9, 20, 23, 59, 59, 999); // 20 October 2026
    if (new Date() <= campaignEnd) {
      const key = `nps_${me?.identifier}_${me?.profile?.username}`;
      const displayedPopUp = getCookie(key);

      if (!displayedPopUp) {
        setCookie(key, true, { expires: 7 });
        setShowModal(true);
      }
    }
      // Show marketing popup until 30 06, 2026, max once every 48 hours per user
    const marketingCampaignEnd = new Date(2026, 5, 30, 23, 59, 59, 999); // month is 0-indexed: 5 = June
    if (
      new Date() <= marketingCampaignEnd &&
      [
        3456, 1633, 338, 6393, 10988, 1975, 5285, 2635, 4082, 2597, 4407, 14218, 2051, 8350, 1362, 878, 319, 2634, 9222,
        5004, 5186, 945, 5348, 2906, 6704, 6988, 3435, 8533, 9177, 9179, 741, 4724, 3091,
      ].includes(me?.identifier)
    ) {
      const marketingKey = `marketing_${me?.identifier}_${me?.profile?.username}`;
      const displayedMarketing = getCookie(marketingKey);

      if (!displayedMarketing) {
        // (2 days)
        setCookie(marketingKey, true, { expires: 2 });
        setShowMarketingModal(true);
      }
    }
  }, [me]);

  const handleCloseModal = () => setShowModal(false);
  const handleCloseMarketingModal = () => setShowMarketingModal(false);

  const isInDashboardTab = useMemo(() => location.pathname.includes('dashboards'), [location.pathname]);

  const featureModal = useMemo(() => {
    if (features.includes('VISTORIA_VISITA_DIGITAL')) {
      return contracts.pending.length === 0 ? <FeatureModal feature="visitaDigital" /> : null;
    }
    return null;
  }, [features, contracts.pending]);

  const clientFormUrlWithIdentifier = useMemo(() => {
    return CLIENT_FORM_URL.replace(
      '{clientId}',
      `${me?.identifier};${btoa(me?.profile?.email)}`
    );
  }, [me]);

  return (
    <div className="flex flex-col min-h-screen pb-[15px] bg-[url('/assets/img/bg-client.png')] bg-bottom bg-no-repeat bg-[length:100%]">
      <ApiContext.Provider value={{ status: apiStatus }}>
        {featureModal}

        <AcceptedTermsModal contracts={contracts} />

        <Header userName={userName} balance={balance} features={features} client />

        <ClientMenu />

        <Grid container justify="center" alignItems="flex-start" className="mt-[12px] mx-auto">
          <Grid item lg={10} md={12}>
            <NewRvPanelFeatureBar clientDocument={me?.clientDocument} />
          </Grid>
        </Grid>

        <main className="flex-1 w-full flex flex-col h-full">
          <Grid container justify="center" alignItems="flex-start" className="mx-auto w-full h-full flex-1">
            <Grid item xs={12} lg={10} md={12} className="h-full">
              <div 
                className="mx-auto bg-white rounded border-2 border-solid border-[#dedede]"
                style={{ paddingTop: isInDashboardTab ? 0 : '20px', marginTop: '15px' }}
              >
                <Routes>
                  <Route path="dashboards" element={<UserDashboardsWrapper />} />
                  <Route index element={<ClientOrders component="orders-list" />} />
                  <Route path="new/*" element={<ClientNewOrder features={features} />} />
                  <Route path="success" element={<ClientFormSuccess />} />
                  <Route path="financial-extract" element={<ClientFinancialSummary />} />
                  <Route path=":orderId/extra-actions" element={<ClientOrders component="order-extra-actions" />} />
                  <Route path=":orderId/checklist" element={<ClientOrders component="checklist-form" />} />
                  <Route path="extra-actions" element={<ClientOrders component="order-extra-actions" />} />
                  <Route path="checklist" element={<ClientOrders component="checklist-form" />} />
                  <Route path=":orderId/repair-budget" element={<ClientOrders component="repair-budget-form" />} />
                  <Route path=":orderId" element={<ClientOrders component="order-details" />} />
                  <Route path=":orderId/edit" element={<ClientOrders component="order-edit" />} />
                </Routes>
              </div>
            </Grid>
          </Grid>
        </main>

        <Dialog open={showModal} maxWidth="sm" onClose={handleCloseModal}>
          <button
            className="bg-transparent border-none cursor-pointer p-0 m-0"
            type="button"
            onClick={handleCloseModal}
          >
            <div className="w-full h-full overflow-hidden">
              <iframe
                width="720px"
                height="720px"
                title={CLIENT_FORM_TITLE}
                style={{ border: 'none', maxWidth: '100%', maxHeight: '100vh' }}
                src={clientFormUrlWithIdentifier}
                frameBorder="0"
                marginWidth="0"
                marginHeight="0"
                allowFullScreen
              />
            </div>
          </button>
        </Dialog>

        <MarketingModal
          open={showMarketingModal}
          onClose={handleCloseMarketingModal}
        />
      </ApiContext.Provider>
    </div>
  );
};

ClientApp.propTypes = {
  balance: PropTypes.string.isRequired,
  contracts: PropTypes.object.isRequired,
  features: PropTypes.arrayOf(PropTypes.string).isRequired,
  userName: PropTypes.string.isRequired,
};

export default ClientApp;
